import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { Badge } from "common/ui/Badge";
import { Button } from "common/ui/Button";
import { Card } from "common/ui/Card";
import { Heading } from "common/ui/Heading";
import { Label } from "common/ui/Label";
import { Paragraph } from "common/ui/Paragraph";
import { Caption } from "common/ui/Caption";
import { Icon } from "common/ui/Icon";

import { IListingLookupDto, ListingTier, ListingType } from "common/middleware/api.services";
import { currencyFormatLocale, formatSquareRoot, sqMetersToSqFeet } from "common/utils/utils";
import { toEnumValue } from "common/utils/toEnumValue";
import styles from "./ListingCard.module.scss";

interface Props {
  listing: IListingLookupDto;
  onClick?: () => void;
  onMakeOffer?: () => void;
  onRent?: () => void;
  onChat?: () => void;
}

function ListingCard({ listing, onClick, onMakeOffer, onRent, onChat }: Props) {
  const { locale } = useRouter();
  const { t } = useTranslation("common");

  // ListingLookupDto.propertyType maps from the same Listing.ListingType enum as ListingDto.listingType; DTO field names differ but values are identical.
  const isLand = toEnumValue(ListingType, listing.propertyType) === ListingType.Land;

  const isAvailableForSale = listing.isAvailableForSale ?? true;
  const isAvailableForRent = listing.isAvailableForRent ?? false;
  const isRentOnly = isAvailableForRent && !isAvailableForSale;
  const isBoth = isAvailableForSale && isAvailableForRent;

  const offeringPrefix = isRentOnly
    ? t("listingCard.forRent")
    : isBoth
    ? t("listingCard.saleAndRent")
    : t("listingCard.forSale");

  const getPropertyTypeLabel = (listing: IListingLookupDto): string => {
    if (listing.propertyType == null) return offeringPrefix;
    const typeLabel = t(`propertyInfoInput.${listing.propertyType}`, { defaultValue: "" });
    return typeLabel ? `${offeringPrefix} ${typeLabel}` : offeringPrefix;
  };

  const offeringPillClass = isRentOnly
    ? `${styles.OfferingPill} ${styles.OfferingPillRent}`
    : isBoth
    ? `${styles.OfferingPill} ${styles.OfferingPillBoth}`
    : `${styles.OfferingPill} ${styles.OfferingPillSale}`;

  const formattedRent = listing.monthlyRent != null
    ? t("listingCard.perMonth", { amount: currencyFormatLocale(listing.monthlyRent, locale) })
    : null;

  return (
    <Card className={styles.ListingCard} onClick={onClick}>
      <div className={styles.ImageContainer}>
        {listing.displayImage?.url ? (
          <Image
            src={listing.displayImage.url}
            alt={listing.name || listing.address?.street || ""}
            fill
            sizes="(max-width: 490px) 100vw, (max-width: 768px) 50vw, (max-width: 1040px) 33vw, 25vw"
          />
        ) : (
          <div className={styles.Placeholder}>
            <Icon icon="image" />
          </div>
        )}
        <div className={offeringPillClass}>{offeringPrefix}</div>
      </div>
      <div className={styles.Content}>
        <div className={styles.PriceRow}>
          <Heading variant="h3">
            {isRentOnly && formattedRent
              ? formattedRent
              : currencyFormatLocale(listing.price, locale)}
          </Heading>
          {toEnumValue(ListingTier, listing.tier) === ListingTier.Standard && (
            <Badge className={styles.VerifiedBadge}>
              <div className={styles.VerifiedIcons}>
                <Icon icon="check" className={styles.VerifiedCheck} />
                <Icon icon="logo" className={styles.VerifiedLogo} />
              </div>
              <Label variant="l2">{t("listingCard.verified")}</Label>
            </Badge>
          )}
        </div>
        <Paragraph variant="p2">
          {getPropertyTypeLabel(listing)}
        </Paragraph>
        {isBoth && formattedRent && (
          <Caption className={styles.AlsoForRent}>
            {t("listingCard.alsoForRent", { amount: currencyFormatLocale(listing.monthlyRent, locale) })}
          </Caption>
        )}
        {listing.address && (
          <>
            <Caption>{listing.address.street}</Caption>
            <Caption>{listing.address.city}</Caption>
          </>
        )}
        <div className={styles.Stats}>
          {!isLand && listing.bedroomQuantity != null && (
            <div className={styles.Stat}>
              <Caption>{listing.bedroomQuantity} {t("listingCard.beds")}</Caption>
            </div>
          )}
          {!isLand && listing.bathroomQuantity != null && (
            <div className={styles.Stat}>
              <Caption>{listing.bathroomQuantity} {t("listingCard.baths")}</Caption>
            </div>
          )}
          {isLand ? (
            listing.totalListingAreaInSqMeters != null && (
              <div className={styles.Stat}>
                <Caption>
                  {formatSquareRoot(sqMetersToSqFeet(listing.totalListingAreaInSqMeters))} {t("listingCard.lotSizeSqft")}
                </Caption>
              </div>
            )
          ) : (
            listing.indoorSquareFeet != null && (
              <div className={styles.Stat}>
                <Caption>{Math.round(listing.indoorSquareFeet)} {t("listingCard.sqft")}</Caption>
              </div>
            )
          )}
        </div>
        {(onMakeOffer || onRent || onChat || listing.externalSourceLogoImage) && (
          <div className={styles.Actions}>
            {isAvailableForSale && onMakeOffer && (
              <Button
                variant="tertiary"
                noIndent
                noSubmit
                onClick={(e) => { e.stopPropagation(); onMakeOffer(); }}
              >
                <Icon icon="currencyDollar" />
                {t("listingCard.makeOffer")}
              </Button>
            )}
            {isAvailableForRent && onRent && (
              <Button
                variant="tertiary"
                noIndent
                noSubmit
                onClick={(e) => { e.stopPropagation(); onRent(); }}
              >
                <Icon icon="currencyDollar" />
                {t("listingCard.rent")}
              </Button>
            )}
            {onChat && (
              <Button
                variant="tertiary"
                noIndent
                noSubmit
                onClick={(e) => { e.stopPropagation(); onChat(); }}
              >
                <Icon icon="chatBubble" />
                {t("listingCard.chat")}
              </Button>
            )}
            {listing.externalSourceLogoImage?.filePath && (
              <img
                src={listing.externalSourceLogoImage.filePath}
                alt={listing.externalSourceName || "External source"}
                className={styles.ExternalSourceLogo}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export { ListingCard };
